import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AuditIconComponent } from '../../icons/audit-icon/audit-icon.component';
import { EditComponent } from '../../icons/edit/edit.component';
import { CommonModule } from '@angular/common';
import { DetailsIconComponent } from '../../icons/details-icon/details-icon.component';
import { DetailsViewConfiguration } from '../../../../shared/models/internal/details/details-view-configuration';
import { EditComponentType } from '../../../../shared/enums/edit-component-type';

import { Policy } from '../../../../shared/models/info/policy';
import { EnabledByPolicyDirective } from '../../../directives/enabled-by-policy.directive';
import { AgentActionDirective } from '../../../directives/agent-action.directive';
import { Dtc } from '../../../../shared/models/dtc/dtc';
import { ErrorMessage } from '../../../../shared/models/error-message/error-message';
import { EntityDetailsViewComponent } from '../details/entity-details-view/entity-details-view.component';

import { LinkIconComponent } from '../../icons/link-icon/link-icon.component';
import { DtcSelectionOverviewComponent } from '../../overviews/dtc/dtc-selection-overview/dtc-selection-overview.component';
import { ErrorCode } from '../../../../shared/models/error-code/error-code';
import { EditErrorCodeComponent } from '../../pages/error-code/edit-error-code/edit-error-code.component';
import { SwPartNumber } from '../../../../shared/models/sw-part-number/sw-part-number';
import { EditSwPartNumberComponent } from '../../pages/sw-part-number/edit-sw-part-number/edit-sw-part-number.component';
import { SwPartNumberUpdateConfirmation } from '../../../../shared/models/sw-part-number/sw-part-number-update-confirmation';
import { LanguageIconComponent } from '../../icons/language-icon/language-icon.component';
import { TranslationStateComponent } from '../language/translation-state/translation-state.component';
import { LanguageTranslationState } from '../../../../shared/models/dtc/language-translation-state';
import { TimelineOverviewComponent } from '../../overviews/audit-log/timeline-overview/timeline-overview.component';
import { AuditLogQuery } from '../../../../shared/models/audit/audit-log-query';
import { SidebarConfiguration } from '../../../../shared/internal/sidebar/sidebar-configuration';
import { LanguageConfigurationComponent } from '../../nw-number-error-memory/language-configuration/language-configuration.component';
import { LanguageConfigurationIconComponent } from '../../icons/language-configuration-icon/language-configuration-icon.component';
import { NwNumberErrorMemory } from '../../../../shared/models/nw-number/nw-number-error-memory';
import { SIDEBAR_AREA } from '../../../../shared/constants/sidebar-area';
import { HandRaisedIconComponent } from '../../icons/hand-raised-icon/hand-raised-icon.component';
import { DetailedTranslationStateComponent } from '../language/detailed-translation-state/detailed-translation-state.component';
import { MergeIcon } from '../../icons/merge-icon/merge-icon';
import { DtcEditComponent } from '../../pages/dtc/dtc-edit/dtc-edit.component';
import { EditErrorMessageComponent } from '../../pages/error-message/edit-error-message/edit-error-message.component';
import { ReviewRequestEditComponent } from '../review-requests/review-request-edit/review-request-edit.component';
import { ReviewRequestMergeComponent } from '../review-requests/review-request-merge/review-request-merge.component';
import { NwNumber } from '../../../../shared/models/nw-number/nw-number';
import { Project } from '../../../../shared/models/project';
import { EditProjectComponent } from '../../pages/project/edit-project/edit-project.component';
import { EditNwNumber } from '../../nw-number/edit-nw-number/edit-nw-number.component';

@Component({
  selector: 'app-sidebar',
  imports: [
    AuditIconComponent,
    EditComponent,
    CommonModule,
    DetailsIconComponent,
    DtcEditComponent,
    EditErrorMessageComponent,
    EnabledByPolicyDirective,
    EntityDetailsViewComponent,
    LinkIconComponent,
    DtcSelectionOverviewComponent,
    EditErrorCodeComponent,
    EditSwPartNumberComponent,
    LanguageIconComponent,
    TranslationStateComponent,
    TimelineOverviewComponent,
    LanguageConfigurationComponent,
    LanguageConfigurationIconComponent,
    ReviewRequestEditComponent,
    HandRaisedIconComponent,
    ReviewRequestMergeComponent,
    DetailedTranslationStateComponent,
    MergeIcon,
    EditProjectComponent,
    EditNwNumber,
    AgentActionDirective,
  ],
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnChanges {
  @Input() public sideBarTitle = '';
  @Input() public detailsViewConfig?: DetailsViewConfiguration;
  @Input() public languageTranslationStates?: LanguageTranslationState[];
  @Input() public detailedLanguageTranslationStatesDocumentation?: LanguageTranslationState[];
  @Input() public detailedLanguageTranslationStatesTerminal?: LanguageTranslationState[];
  @Input() public editComponentType?: EditComponentType;
  @Input() public editComponentId?: string;
  @Input() public dtc?: Dtc;
  @Input() public errorMessage?: ErrorMessage;
  @Input() public errorCode?: ErrorCode;
  @Input() public swPartNumber?: SwPartNumber;
  @Input() public nwNumberErrorMemory?: NwNumberErrorMemory;
  @Input() public nwNumber?: NwNumber;
  @Input() public project?: Project;
  /**
   * Configuration to enable/disable specific sidebar areas
   */
  @Input() public sidebarConfiguration?: SidebarConfiguration;
  @Output() public closeSidebarTriggered = new EventEmitter<void>();
  @Output() public updatedDtc = new EventEmitter<Dtc>();
  @Output() public updatedErrorMessage = new EventEmitter<ErrorMessage>();
  @Output() public updatedErrorCode = new EventEmitter<ErrorCode>();
  @Output() public updatedSwPartNumber = new EventEmitter<SwPartNumberUpdateConfirmation>();
  @Output() public updatedProject = new EventEmitter<Project>();
  @Output() public updatedNwNumber = new EventEmitter<NwNumber>();

  public editComponent = EditComponentType;
  public policy = Policy;
  public auditLogQuery?: AuditLogQuery;
  public sidebarArea = SIDEBAR_AREA;

  /** Maps the current edit component type to a stable business object name for the agent layer. */
  public get agentBusinessObject(): string | undefined {
    switch (this.editComponentType) {
      case EditComponentType.Dtc:
        return 'dtc';
      case EditComponentType.ErrorMessage:
        return 'errorMessage';
      case EditComponentType.ErrorCode:
        return 'errorCode';
      case EditComponentType.SwPartNumber:
        return 'swPartNumber';
      case EditComponentType.NwNumber:
        return 'nwNumber';
      case EditComponentType.Project:
        return 'project';
      default:
        return undefined;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['dtc'] && this.dtc) {
      this.auditLogQuery = {
        parentEntityId: this.dtc.parentId,
        entityId: this.dtc.id,
        parentEndingAt: this.dtc.createdOn,
      };
    } else if (changes['errorMessage'] && this.errorMessage) {
      this.auditLogQuery = {
        parentEntityId: this.errorMessage.parentId,
        entityId: this.errorMessage.id,
        parentEndingAt: this.errorMessage.createdOn,
      };
    } else if (changes['errorCode'] && this.errorCode) {
      this.auditLogQuery = {
        entityId: this.errorCode.id,
      };
    } else if (changes['swPartNumber'] && this.swPartNumber) {
      this.auditLogQuery = {
        parentEntityId: this.swPartNumber.parentId,
        entityId: this.swPartNumber.id,
        parentEndingAt: this.swPartNumber.createdOn,
      };
    } else if (changes['nwNumber'] && this.nwNumber) {
      this.auditLogQuery = {
        entityId: this.nwNumber.id,
      };
    } else if (changes['project'] && this.project) {
      this.auditLogQuery = {
        entityId: this.project.id,
      };
    }
  }

  public closeSidebar(): void {
    this.closeSidebarTriggered.emit();
  }

  public onUpdatedDtc(dtc: Dtc): void {
    this.updatedDtc.emit(dtc);
  }

  public onUpdatedErrorMessage(errorMessage?: ErrorMessage): void {
    this.updatedErrorMessage.emit(errorMessage);
  }

  public onUpdatedErrorCode(errorCode: ErrorCode): void {
    this.updatedErrorCode.emit(errorCode);
  }

  public onUpdatedSwPartNumber(swPartNumber: SwPartNumberUpdateConfirmation): void {
    this.updatedSwPartNumber.emit(swPartNumber);
  }

  public onUpdatedProject(project: Project): void {
    this.updatedProject.emit(project);
  }

  protected onUpdatedNwNumber(nwNumber: NwNumber): void {
    this.updatedNwNumber.emit(nwNumber);
  }
}
